import { DataSnapshot } from 'firebase/database';

import { PatientSites } from '@lib';
import { DataSourceConfig } from '@stores';
import {
  AssetCollection,
  ChatMessage,
  ExpectedCareTaskAnswerObjectByBackend,
  Medication,
  MedicationPlan,
  Patient,
  PatientConnectionStatus,
  Site,
  TypingStatusObject,
  UnReadMessages,
  WearableDataObject,
  WearableScopes,
  WithingsTokens,
} from '@type';

import {
  AssetGateway,
  AuthGateway,
  ChatGateway,
  DeviceGateway,
  MedicationGateway,
  PatientGateway,
  ReminderGateway,
  SiteGateway,
  SocialMediaGateway,
  SourceGateway,
  StatsGateway,
  TaskGateway,
  WearableGateway,
} from './gateways';
import { PatientSettingsPayload, ReminderNodeValue } from './types';

export class DataGatewayService {
  private readonly authGateway: AuthGateway;
  private readonly deviceGateway: DeviceGateway;
  private readonly patientGateway: PatientGateway;
  private readonly siteGateway: SiteGateway;
  private readonly sourceGateway: SourceGateway;
  private readonly taskGateway: TaskGateway;
  private readonly medicationGateway: MedicationGateway;
  private readonly reminderGateway: ReminderGateway;
  private readonly wearableGateway: WearableGateway;
  private readonly chatGateway: ChatGateway;
  private readonly assetGateway: AssetGateway;
  private readonly statsGateway: StatsGateway;
  private readonly socialMediaGateway: SocialMediaGateway;

  constructor(config: DataSourceConfig) {
    this.authGateway = new AuthGateway(config);
    this.deviceGateway = new DeviceGateway(config);
    this.patientGateway = new PatientGateway(config);
    this.siteGateway = new SiteGateway(config);
    this.sourceGateway = new SourceGateway(config);
    this.taskGateway = new TaskGateway(config);
    this.medicationGateway = new MedicationGateway(config);
    this.reminderGateway = new ReminderGateway(config);
    this.wearableGateway = new WearableGateway(config);
    this.chatGateway = new ChatGateway(config);
    this.assetGateway = new AssetGateway(config);
    this.statsGateway = new StatsGateway(config);
    this.socialMediaGateway = new SocialMediaGateway(config);
  }

  exchangeSession = async () => this.authGateway.exchangeSession();
  getSessionValidity = async () => this.authGateway.getSessionValidity();
  endSession = async () => this.authGateway.endSession();

  setDeviceIdentifier = (uuid: string) =>
    this.deviceGateway.setDeviceIdentifier(uuid);
  isNewDevice = () => this.deviceGateway.isNewDevice();
  subscribeToDeviceIdentifierChange = (callback: () => unknown) =>
    this.deviceGateway.subscribeToDeviceIdentifierChange(callback);
  syncDeviceOS = () => this.deviceGateway.syncDeviceOS();
  syncDeviceToken = (token?: string) =>
    this.deviceGateway.syncDeviceToken(token);
  syncDeviceLang = () => this.deviceGateway.syncDeviceLang();
  clearDeviceToken = () => this.deviceGateway.clearDeviceToken();
  markNotificationsRead = () => this.deviceGateway.markNotificationsRead();

  getPatientSites = async () => this.patientGateway.getPatientSites();
  subscribeToPatientSites = (callback: (sites: PatientSites) => void) =>
    this.patientGateway.subscribeToPatientSites(callback);
  getPatientInstances = async () => this.patientGateway.getPatientInstances();
  getIsPatientDeleted = async () => this.patientGateway.getIsPatientDeleted();
  getPatientProfileData = async () =>
    this.patientGateway.getPatientProfileData();
  getInsuranceTypes = async () => this.patientGateway.getInsuranceTypes();
  subscribeToAccountReset = (callback: () => unknown) =>
    this.patientGateway.subscribeToAccountReset(callback);

  getSiteInfoByUuid = (siteUuid: string) =>
    this.siteGateway.getSiteInfoByUuid(siteUuid);
  getSiteInfoById = (siteId: number) =>
    this.siteGateway.getSiteInfoById(siteId);
  handShake = async (
    siteUuid: string,
    isFirstScan: boolean = false,
    isFromQrCode: boolean = false,
    isHandshakeWithoutPrivacyPolicyConsent: boolean = false
  ) =>
    this.siteGateway.handShake(
      siteUuid,
      isFirstScan,
      isFromQrCode,
      isHandshakeWithoutPrivacyPolicyConsent
    );

  authenticate = async () => this.sourceGateway.authenticate();
  getMaintenanceMode = async () => this.sourceGateway.getMaintenanceMode();
  subscribeToMaintenanceMode = (callback: () => unknown) =>
    this.sourceGateway.subscribeToMaintenanceMode(callback);
  getServerVersion = async () => this.sourceGateway.getServerVersion();
  subscribeToShouldUpdateApp = (callback: () => unknown) =>
    this.sourceGateway.subscribeToShouldUpdateApp(callback);
  getTasksSummary = (siteUuid: string) =>
    this.taskGateway.getTasksSummary(siteUuid);
  getTaskDetails = (taskId: number, siteUuid: string) =>
    this.taskGateway.getTaskDetails(taskId, siteUuid);
  subscribeToTasksUpdateEvent = (callback: (snapshot: DataSnapshot) => void) =>
    this.taskGateway.subscribeToTasksUpdateEvent(callback);
  submitCareTaskAnswer = (
    answerObject: ExpectedCareTaskAnswerObjectByBackend
  ) => this.taskGateway.submitCareTaskAnswer(answerObject);
  uploadAnswerFile = async (file: File) =>
    this.taskGateway.uploadAnswerFile(file);

  validatePatientEmail = async (email: string) =>
    this.authGateway.validatePatientEmail(email);
  createAccount = async (params: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  }) => this.authGateway.createAccount(params);
  resendOTP = async () => this.authGateway.resendOTP();
  verifyOTP = async (otp: string, isResend = false) =>
    this.authGateway.verifyOTP(otp, isResend);
  addTrustedSite = async (site: Site) => this.authGateway.addTrustedSite(site);

  subscribeToReminders = (callback: (values: ReminderNodeValue[]) => unknown) =>
    this.reminderGateway.subscribeToReminders(callback);
  subscribeToRemindersRemoved = (
    callback: (snapshot: ReminderNodeValue) => unknown
  ) => this.reminderGateway.subscribeToRemindersRemoved(callback);
  requestReminders = async () => this.reminderGateway.requestReminders();
  removeSiteFromTrustedInstallation = async (siteUuid: string) =>
    this.authGateway.removeSiteFromTrustedInstallation(siteUuid);
  initiateClassicOnboarding = async (params: {
    qrCodeUUID: string;
    projectId: string;
    siteUUID: string;
  }) => this.authGateway.initiateClassicOnboarding(params);
  updatePatientConnectionStatus = async (status: PatientConnectionStatus) =>
    this.patientGateway.updatePatientConnectionStatus(status);
  updatePatientProfileData = async (patientProfile: Partial<Patient>) => {
    await this.patientGateway.updatePatientProfileData(patientProfile);
  };
  uploadPatientProfilePicture = async (patientProfilePicture: File) => {
    return await this.patientGateway.uploadPatientProfilePicture(
      patientProfilePicture
    );
  };
  checkPatientRegistrationStatus = async () => {
    return await this.authGateway.checkPatientRegistrationStatus();
  };

  answerMedication = async (
    medication: Medication,
    isPreviouslyChecked: boolean
  ) => this.medicationGateway.answerMedication(medication, isPreviouslyChecked);
  sendMedicationPlan = async (medicationPlan: MedicationPlan[]) =>
    this.medicationGateway.sendMedicationPlan(medicationPlan);
  archiveMedicationPlan = async () =>
    this.medicationGateway.archiveMedicationPlan();
  getActiveMedicationPlanAsset = async () =>
    this.medicationGateway.getActiveMedicationPlanAsset();
  subscribeToMedications = (
    callback: (medications: Medication[], isMedicationPlan: boolean) => unknown
  ) => this.medicationGateway.subscribeToMedications(callback);
  subscribeToMedicationsRemoved = (
    callback: (medications: Medication[]) => unknown
  ) => this.medicationGateway.subscribeToMedicationsRemoved(callback);

  submitWearableHealthData = (answerObject: WearableDataObject) =>
    this.wearableGateway.submitWearableHealthData(answerObject);
  getPatientWearables = async () => this.wearableGateway.getPatientWearables();

  requestChatHistory = (siteUuid: string) =>
    this.chatGateway.requestChatHistory(siteUuid);

  subscribeToChatHistory = (
    callback: (message: ChatMessage[] | null, site: Site) => void
  ) => this.chatGateway.subscribeToChatHistory(callback);
  uploadFile = async (file: File) => this.chatGateway.uploadFile(file);

  sendChatMessage = (siteUuid: string, message: string) =>
    this.chatGateway.sendChatMessage(siteUuid, message);
  subscribeToChatMessage = (
    siteUUID: string,
    callback: (
      message: ChatMessage | null,
      site: Site,
      key: string | null
    ) => void
  ) => this.chatGateway.subscribeToChatMessage(siteUUID, callback);
  subscribeToUnReadChatMessage = (
    callback: (unReadMessages: UnReadMessages) => void
  ) => this.chatGateway.subscribeToUnReadChatMessage(callback);
  removeChatHistoryNode = () => this.chatGateway.removeChatHistoryNode();
  removeChatMessageNode = (key: string) =>
    this.chatGateway.removeChatMessageNode(key);
  sendTypingStatus = (status: TypingStatusObject) =>
    this.chatGateway.sendTypingStatus(status);
  removeTypingStatusNode = () => this.chatGateway.removeTypingStatusNode();
  subscribeToTypingStatus = (
    callback: (snapshot: TypingStatusObject) => void
  ) => this.chatGateway.subscribeToTypingStatus(callback);
  subscribeToTypingStatusRemoved = (
    callback: (snapshot: TypingStatusObject) => void
  ) => this.chatGateway.subscribeToTypingStatusRemoved(callback);

  saveWithingsTokens = async (
    withingsTokens: WithingsTokens,
    scopes: WearableScopes
  ) => this.wearableGateway.saveWithingsTokens(withingsTokens, scopes);
  getWithingsTokens = async () => this.wearableGateway.getWithingsTokens();
  withingsRollback = async () => this.wearableGateway.withingsRollback();
  getPatientSettings = async () => this.patientGateway.getPatientSettings();
  updatePatientSettings = async (patientSettings: PatientSettingsPayload) =>
    this.patientGateway.updatePatientSettings(patientSettings);
  deletePatientAccount = async () =>
    await this.authGateway.deletePatientAccount();
  sendLogoutRequest = async ({
    shouldLogoutFromAllSites,
    siteUuid,
  }: {
    shouldLogoutFromAllSites: boolean;
    siteUuid?: string;
  }) =>
    await this.authGateway.sendLogoutRequest({
      shouldLogoutFromAllSites,
      siteUuid,
    });
  clearDeviceOS = async () => await this.deviceGateway.clearDeviceOS();
  clearDeviceTokenAndLang = async () =>
    await this.deviceGateway.clearDeviceTokenAndLang();

  requestAssetList = (siteUuid: string, siteId: number, search: string) =>
    this.assetGateway.requestAssetList(siteUuid, siteId, search);

  subscribeToAssetAdded = (
    site: Site,
    callback: (assets: AssetCollection | null) => void
  ) => this.assetGateway.subscribeToAssetAdded(site, callback);

  requestSubmittedTasksData = (siteUuid: string) =>
    this.statsGateway.requestSubmittedTasksData(siteUuid);
  requestHealthData = (siteUuid: string) =>
    this.statsGateway.requestHealthData(siteUuid);

  getSocialMediaPosts = (siteUuid: string, siteId: number) =>
    this.socialMediaGateway.getSocialMediaPosts(siteUuid, siteId);
}
